import { Inbox, MessageSquare, Settings, Search, Home, Folder } from 'lucide-react'
import type { CommandItem } from './command-palette.types'
import type { MaaterAppRouteInfo } from '@/lib/maater-document-url'
import type { UrlIntent } from '@/lib/app-routes'
import { getThread } from '@/lib/api/threads'

function routeLabel(intent: UrlIntent): string {
  switch (intent.type) {
    case 'inbox':
      return 'Inbox'
    case 'flow-studio':
      return intent.surface.charAt(0).toUpperCase() + intent.surface.slice(1)
    case 'inbox-thread':
      return 'Inbox thread'
    case 'chat-conversation':
      return 'Chat'
    case 'search':
      return intent.view === 'activity' ? 'Activity' : 'Search'
    case 'settings':
      return 'Settings'
    case 'workspace-home':
      return intent.workspaceSlug
    case 'explorer':
      return intent.folderPath || 'Browse'
    case 'folder-entity':
      return 'Folder'
    case 'extension-basecamp':
      return 'Basecamp'
    case 'extension':
      return intent.extensionId || 'Extension'
    default:
      return 'page'
  }
}

async function threadTitle(threadId: string): Promise<string | null> {
  try {
    const thread = await getThread(threadId)
    const title = String(thread.title || '').trim()
    return title || null
  } catch {
    return null
  }
}

/** Primary palette rows for a pasted in-app Maater URL that is not a document. */
export async function buildMaaterAppRouteRows(
  route: MaaterAppRouteInfo,
): Promise<CommandItem[]> {
  const { intent, pathname } = route

  if (intent.type === 'inbox-thread') {
    const title = (await threadTitle(intent.threadId)) || 'Inbox thread'
    return [
      {
        id: `open-inbox-thread-${intent.threadId}`,
        title: `Open ${title}`,
        type: 'action',
        icon: <Inbox className="h-4 w-4" />,
        subtitle: 'Inbox thread',
        action: 'open-inbox-thread',
        matchScore: 250,
        matchType: 'maater-url',
        inboxThreadId: intent.threadId,
      },
    ]
  }

  if (intent.type === 'chat-conversation') {
    const title = (await threadTitle(intent.threadId)) || 'Chat'
    return [
      {
        id: `open-chat-${intent.threadId}`,
        title: `Open ${title}`,
        type: 'action',
        icon: <MessageSquare className="h-4 w-4" />,
        subtitle: 'Chat',
        action: 'open-chat-conversation',
        matchScore: 250,
        matchType: 'maater-url',
        chatThreadId: intent.threadId,
      },
    ]
  }

  if (intent.type === 'inbox') {
    return [
      {
        id: 'open-inbox-index',
        title: 'Open Inbox',
        type: 'action',
        icon: <Inbox className="h-4 w-4" />,
        subtitle: 'Folders index',
        action: 'open-inbox',
        matchScore: 250,
        matchType: 'maater-url',
      },
    ]
  }

  const Icon =
    intent.type === 'settings'
      ? Settings
      : intent.type === 'search'
        ? Search
        : intent.type === 'workspace-home'
          ? Home
          : Folder

  return [
    {
      id: `open-app-path-${pathname}`,
      title: `Open ${routeLabel(intent)}`,
      type: 'action',
      icon: <Icon className="h-4 w-4" />,
      subtitle: pathname,
      action: 'open-app-path',
      matchScore: 250,
      matchType: 'maater-url',
      routePathname: pathname,
    },
  ]
}

export function isNonDocumentMaaterRoute(intent: UrlIntent): boolean {
  return intent.type !== 'document' && intent.type !== 'legacy-redirect'
}
